import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/joy';
import { t } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactGroupMode } from 'contacts/components/AddContactGroupByEmail/enums';
import { useAddContactGroupByEmailContext } from 'contacts/components/AddContactGroupByEmail/state';
import { ContactGroupType } from 'contacts/enums';
import { contactGroupService } from 'contacts/services';
import { IContactGroup } from 'contacts/types';
import { Confirmation, FormField, RadioGroupInput } from 'core/components';
import { NotificationElement } from 'core/components/Notification';
import { LabeledValue } from 'core/elements';
import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { Color, Level, Variant } from 'theme/enums';

import { AddContactGroup } from './AddContactGroup';

export const ContactGroupDetails = () => {
  const { t } = useTranslation();

  const {
    contactEmail,
    contactGroup,
    contactGroupRef,
    contactGroupLocked,
    contactGroupMode,
    genericEmail,
    contactGroupType,
    attachDomainConfirmation,
    onContactGroupChoose,
    onContactGroupModeChange,
    onAttachDomainConfirm,
    onAttachDomainCancel,
  } = useAddContactGroupByEmailContext();

  const emailDomain = useMemo(() => {
    return contactEmail?.split('@')[1]?.toLowerCase() || '';
  }, [contactEmail]);

  const modeChoices = useMemo(() => {
    return [
      {
        value: ContactGroupMode.SELECT_EXISTING,
        label: t('labelAddToExistingCompany'),
      },
      {
        value: ContactGroupMode.CREATE_NEW,
        label: t('labelCreateNewCompany'),
      },
    ];
  }, [t]);

  if (contactGroupLocked) {
    return (
      <Stack gap={2}>
        <NotificationElement
          title={t('titleContactGroupAlreadyExists')}
          message={t('messageConfirmExistingContactGroup')}
          type={ApiStatus.INFO}
          position={NotificationPosition.DRAWER}
          showNotification
          withoutCloseButton
        />

        <Card variant={Variant.PLAIN} color={Color.NEUTRAL}>
          <CardContent sx={{ display: 'flex', gap: 1 }}>
            <Typography level={Level.TITLE_LG}>{contactGroup?.name}</Typography>

            <Stack gap={0.5} mt={0.5}>
              {getContactGroupInfo(contactGroup).map((info) => (
                <LabeledValue
                  key={info.label}
                  label={info.label}
                  value={info.value}
                  options={{
                    labelLevel: Level.BODY_SM,
                    labelSx: { fontWeight: 400 },
                    inline: true,
                    wrapperProps: {
                      sx: {
                        display: 'flex',
                        gap: 0.5,
                      },
                    },
                  }}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  const selectExisting = ContactGroupMode.SELECT_EXISTING === contactGroupMode;

  return (
    <Stack gap={2}>
      {genericEmail && (
        <NotificationElement
          title={t('titleGenericEmail')}
          message={
            selectExisting
              ? t('messageSelectContactGroup')
              : t('messageCreateContactGroupForGenericEmail')
          }
          type={ApiStatus.INFO}
          position={NotificationPosition.DRAWER}
          showNotification
          withoutCloseButton
        />
      )}

      <RadioGroupInput
        name="contactGroupMode"
        value={contactGroupMode}
        choices={modeChoices}
        onChange={(event) =>
          onContactGroupModeChange((event.target as HTMLInputElement).value as ContactGroupMode)
        }
      />

      {selectExisting && (
        <>
          <FormField
            name="contactGroup"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              label: t('labelCompany'),
              value: contactGroup,
              required: true,
              getOptions: contactGroupService.getManyAutocomplete,
              requestPayload: {
                filter: {
                  ...(contactGroupType === ContactGroupType.CUSTOMER && {
                    isCustomer: '"1"',
                  }),
                  ...(contactGroupType === ContactGroupType.VENDOR && {
                    isVendor: '"1"',
                  }),
                },
              },
              onChange: (_, option: IContactGroup) => onContactGroupChoose(option),
            }}
          />

          {!genericEmail && contactGroup?.id && (
            <NotificationElement
              title={t('titleContactGroupDomainMismatch')}
              message={t('messageContactGroupDomainMismatch', {
                domain: emailDomain,
                contactGroupName: contactGroup.name,
              })}
              type={ApiStatus.WARNING}
              position={NotificationPosition.DRAWER}
              showNotification
              withoutCloseButton
            />
          )}
        </>
      )}

      {!selectExisting && <AddContactGroup ref={contactGroupRef} level={1} />}

      <Confirmation
        isOpen={attachDomainConfirmation}
        color={Color.WARNING}
        title={t('titleAttachContactGroupDomain')}
        message={t('messageConfirmAttachContactGroupDomain', {
          domain: emailDomain,
          contactGroupName: contactGroup?.name,
        })}
        onCancel={onAttachDomainCancel}
        onConfirm={onAttachDomainConfirm}
        confirmBtnColor={Color.WARNING}
      />
    </Stack>
  );
};

const getContactGroupInfo = (
  contactGroup: IContactGroup
): { label: string; value: string | JSX.Element }[] => {
  const domains = contactGroup?.domains ? (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {contactGroup.domains.map((domain) => (
        <Chip key={domain} variant={Variant.OUTLINED} sx={{ borderRadius: '6px' }}>
          {`@${domain}`}
        </Chip>
      ))}
    </Box>
  ) : null;

  return [
    { label: t('labelWebsite'), value: contactGroup?.website },
    { label: t('labelContactGrouping'), value: contactGroup?.contactGroupingName },
    {
      label: t('labelBaseMargin'),
      value: contactGroup?.baseMargin ? `+${contactGroup.baseMargin}%` : null,
    },
    {
      label: t('labelBaseDiscount'),
      value: contactGroup?.baseDiscount ? `-${contactGroup.baseDiscount}%` : null,
    },
    { label: t('labelDomains'), value: domains },
  ];
};
